// dependencies
import React, { useState } from "react";

import { faArrowLeft, faFloppyDisk, faSearch } from "@fortawesome/pro-regular-svg-icons";

// components
import { Text } from "@performyard/py-components";

import Button from "../../../../../../components/Button";
import TextInput from "../../../../../../components/Inputs/InputText";
import Spacer from "../../../../../../components/Spacer";
import Directory from "./Directory";

// hooks
import { useMutateAddUserToSelectionObject } from "../../../../../../api/hooks";
import useDebouncedValue from "../../../../../../hooks/useDebouncedValue";
import useToast from "../../../../../../hooks/useToast";

// utils
import pluralizer from "../../../../../../utils/pluralizer";

interface AddEmployeesProps {
	setPage: (page: string) => void;
	excludedUsers: string[];
	setExcludedUsers: React.Dispatch<React.SetStateAction<string[]>>;
	selectionObjectID: string;
}

const AddEmployees = (props: AddEmployeesProps) => {
	const { setPage, excludedUsers, setExcludedUsers, selectionObjectID } = props;

	const toast = useToast();

	const [search, setSearch] = useState("");
	const debouncedSearch = useDebouncedValue(search, 250);

	const { mutate: addUserToSelectionObject, isPending: isAddingUsersToSelectionObject } =
		useMutateAddUserToSelectionObject(selectionObjectID, {
			onSuccess: (data) => {
				console.log("data: ", data);
				setPage("main");
				toast(
					`${excludedUsers.length} ${pluralizer(
						excludedUsers.length,
						"Employee has",
						"Employees have"
					)} been added.`,
					{ type: "success", size: "large" }
				);
			},
			onError: () => {
				toast("Unable to add employees.", { type: "error", size: "large" });
			},
		});

	const handleAddUsersToExcludedList = async () => {
		addUserToSelectionObject({ userIDs: excludedUsers });
		setPage("main");
	};

	return (
		<>
			<div className="grid grid-cols-3">
				<div className="justify-start">
					<Button onClick={() => setPage("main")} color="gray" textOnly icon={faArrowLeft}>
						Back
					</Button>
				</div>
				<Text dangerouslySetClassName="text-center" size="paragraph-1" weight="semi-bold">
					Add Members to be Excluded
				</Text>
				<div />
			</div>
			<Spacer height={7} />
			<div className="flex justify-between">
				<TextInput
					value={search}
					onChange={setSearch}
					placeholder="Search employees..."
					icon={faSearch}
					clearable
					className="w-[288px]"
				/>

				<Button
					isRequesting={isAddingUsersToSelectionObject}
					color="green"
					icon={faFloppyDisk}
					iconPosition="left"
					onClick={handleAddUsersToExcludedList}
				>
					Save & Complete
				</Button>
			</div>
			<Spacer height={2} />
			<Directory
				canBulkSelect={true}
				selectedEmployees={excludedUsers}
				setSelectedEmployees={setExcludedUsers}
				employeeSearchProps={{
					searchTerm: debouncedSearch,
					limitSelected: false,
					exclusionListId: selectionObjectID,
				}}
				isLoading={false}
				emptyText={
					debouncedSearch
						? "A search on the employee list results in no matches."
						: "There are currently no excluded employees."
				}
			/>
		</>
	);
};

export default AddEmployees;
